import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import TextInput from '../ui/TextInput';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../ui/Modal';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import { Accordion, AccordionItem } from '../ui/Accordion';
import {
  FolderPlusIcon,
  XIcon,
  CopyIcon,
  DocumentTextIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
} from '../ui/Icons';
import { getColumnHeaderText } from '../DataTable/DataTable';
import useSWR from 'swr';
import {
  getTablePresets,
  createTablePreset,
  updateTablePreset,
  deleteTablePreset,
  setDefaultTablePreset,
  getDefaultTablePreset,
  TablePreset as APITablePreset,
} from '../../services/tablePresetsService';
import { useNotification } from '../../contexts/NotificationContext';
import { APP_CONFIG } from '../../config/appConfig';

interface TablePreset extends APITablePreset {}

type FooterType = 'sum' | 'avg' | 'count' | 'custom';

interface FooterConfig {
  showFooter?: boolean;
  useFilteredData?: boolean; // If true, use filtered data; if false, use all data (default: true)
  footerColumns?: Record<string, {
    footerType: FooterType;
    footerLabel?: string;
  }>;
}

interface TablePresetsProps {
  // Column related
  visibleColumns: Record<string, boolean>;
  onApplyVisibleColumns: (columns: Record<string, boolean>) => void;
  allColumns: Array<{ key: string; header: React.ReactNode }>;

  // Table settings
  tableDensity: 'compact' | 'normal' | 'comfortable';
  onApplyDensity: (density: 'compact' | 'normal' | 'comfortable') => void;

  // Column configuration
  columnWidths?: Record<string, string>;
  columnOrder?: string[];
  /** Custom display names per column key (saved with preset) */
  columnLabels?: Record<string, string>;
  onApplyColumnLabels?: (labels: Record<string, string>) => void;
  sortConfig?: Array<{ key: string; direction: 'asc' | 'desc'; priority?: number }>;
  onApplyTableConfig?: (config: {
    columnWidths?: Record<string, string>;
    columnOrder?: string[];
    sortConfig?: Array<{ key: string; direction: 'asc' | 'desc'; priority?: number }>;
  }) => void;

  // Footer configuration
  footerConfig?: FooterConfig;
  onApplyFooterConfig?: (config: FooterConfig) => void;

  // Component props
  tableId?: string;
  entityName?: string;
  isOpen: boolean;
  onClose: () => void;
  /** When true, render as an inline right panel (no backdrop, no fixed positioning) */
  asPanel?: boolean;

  // Default preset loading
  onLoadDefaultPreset?: (config: TablePreset['config']) => void;

  /** When user has applied a preset and made changes, pass it so we can offer "Update [preset]" */
  lastAppliedPreset?: TablePreset | null;
  /** Called when user applies a preset (so parent can track last applied) */
  onPresetApplied?: (preset: TablePreset) => void;
  /** Called when user resets to defaults (parent can clear last applied preset) */
  onPresetCleared?: () => void;
  
  // Callback when presets change
  onPresetsChange?: () => void;
}

const TablePresets: React.FC<TablePresetsProps> = ({
  visibleColumns = {},
  onApplyVisibleColumns,
  allColumns = [],
  tableDensity = APP_CONFIG.table.defaultDensity,
  onApplyDensity,
  columnWidths = {},
  columnOrder = [],
  columnLabels = {},
  onApplyColumnLabels,
  sortConfig = [],
  onApplyTableConfig,
  footerConfig = { showFooter: false, useFilteredData: true, footerColumns: {} },
  onApplyFooterConfig,
  tableId = 'default-table',
  entityName = 'Table',
  isOpen,
  onClose,
  asPanel = false,
  lastAppliedPreset = null,
  onPresetApplied,
  onPresetCleared,
  onLoadDefaultPreset,
  onPresetsChange,
}) => {
  const { colors } = useTheme();
  const { showNotification } = useNotification();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [editingPreset, setEditingPreset] = useState<TablePreset | null>(null);
  const [activeTab, setActiveTab] = useState<'customize' | 'presets'>('customize');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState<TablePreset | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingColumnKey, setDraggingColumnKey] = useState<string | null>(null);
  const [dragOverColumnKey, setDragOverColumnKey] = useState<string | null>(null);
  const [localColumnOrder, setLocalColumnOrder] = useState<string[]>(columnOrder || []);
  const [columnFilter, setColumnFilter] = useState('');
  /** When user makes changes, config no longer matches a preset; keep showing the last active preset so they can save to it */
  const [stickyActivePreset, setStickyActivePreset] = useState<TablePreset | null>(null);
  /** When duplicating a preset, use this config for the new preset instead of current table config */
  const [duplicateFromConfig, setDuplicateFromConfig] = useState<TablePreset['config'] | null>(null);
  /** Controlled accordion open state for Expand all / Collapse all */
  const [accordionOpen, setAccordionOpen] = useState({
    density: false,
    columns: false,
    sort: false,
    footer: false,
    tips: false,
  });
  const setAccordionSection = (key: keyof typeof accordionOpen, open: boolean) =>
    setAccordionOpen((prev) => ({ ...prev, [key]: open }));
  const expandAllAccordions = () => setAccordionOpen({ density: true, columns: true, sort: true, footer: true, tips: true });
  const collapseAllAccordions = () => setAccordionOpen({ density: false, columns: false, sort: false, footer: false, tips: false });
  /** Inline caption edit state for Visible Columns (similar to Manage Bookmarks) */
  const [editingColumnKeyForLabel, setEditingColumnKeyForLabel] = useState<string | null>(null);
  const [editingColumnLabel, setEditingColumnLabel] = useState<string>('');

  // Keep a local working copy of column order for drag & drop, synced from props
  useEffect(() => {
    const baseOrder =
      (columnOrder && columnOrder.length > 0
        ? columnOrder
        : allColumns.map((c) => String(c.key))) || [];
    setLocalColumnOrder(baseOrder);
  }, [columnOrder, allColumns]);

  const orderedColumns = (() => {
    if (!allColumns || allColumns.length === 0) return [];
    const keyToColumn = new Map(allColumns.map((c) => [String(c.key), c]));
    const fromOrder = localColumnOrder
      .map((key) => keyToColumn.get(key))
      .filter((c): c is { key: string; header: React.ReactNode } => !!c);
    const remaining = allColumns.filter((c) => !localColumnOrder.includes(String(c.key)));
    return [...fromOrder, ...remaining];
  })();

  const handleColumnDrop = (targetKey: string) => {
    if (!draggingColumnKey || draggingColumnKey === targetKey) {
      setDraggingColumnKey(null);
      setDragOverColumnKey(null);
      return;
    }

    setLocalColumnOrder((prev) => {
      const base = prev.length > 0 ? [...prev] : allColumns.map((c) => String(c.key));
      const fromIndex = base.indexOf(draggingColumnKey);
      const toIndex = base.indexOf(targetKey);
      if (fromIndex === -1 || toIndex === -1) return base;
      base.splice(fromIndex, 1);
      base.splice(toIndex, 0, draggingColumnKey);

      if (onApplyTableConfig) {
        onApplyTableConfig({ columnOrder: base });
      }

      return base;
    });

    setDraggingColumnKey(null);
    setDragOverColumnKey(null);
  };

  // Load presets from API using SWR
  const { data: presets = [], mutate: mutatePresets, isLoading: isLoadingPresets } = useSWR<TablePreset[]>(
    isOpen ? `table-presets-${tableId}` : null,
    () => getTablePresets(tableId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  // Track if default preset has been loaded to prevent re-applying
  const defaultPresetLoadedRef = React.useRef(false);

  // Load default preset on mount (only once)
  useEffect(() => {
    if (onLoadDefaultPreset && !defaultPresetLoadedRef.current) {
      const loadDefault = async () => {
        try {
          const defaultPreset = await getDefaultTablePreset(tableId);
          if (defaultPreset) {
            defaultPresetLoadedRef.current = true;
            onLoadDefaultPreset(defaultPreset.config);
          }
        } catch (error) {
          console.warn('Failed to load default preset:', error);
        }
      };
      loadDefault();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId]);

  // Close on outside click (but not when modal is open, or when as panel – resize handle is "outside")
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSaveModal || showDeleteConfirm) {
        return;
      }

      const target = event.target as HTMLElement;
      if (target.closest('[data-table-presets-resize]')) {
        return;
      }

      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (!target.closest('[role="dialog"]') && !target.closest('.fixed.inset-0')) {
          onClose();
        }
      }
    };

    if (isOpen && !showSaveModal && !showDeleteConfirm && !asPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, showSaveModal, showDeleteConfirm, asPanel]);

  // Escape to close panel (when no modal is open)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showSaveModal && !showDeleteConfirm) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, showSaveModal, showDeleteConfirm]);

  // Reset column filter when panel closes
  useEffect(() => {
    if (!isOpen) setColumnFilter('');
  }, [isOpen]);

  // Get current table configuration
  const getCurrentConfig = (): TablePreset['config'] => ({
    visibleColumns: visibleColumns,
    tableDensity: tableDensity,
    columnWidths: columnWidths,
    columnOrder: columnOrder || [],
    columnLabels: Object.keys(columnLabels || {}).length > 0 ? columnLabels : undefined,
    sortConfig: sortConfig || [],
    footerConfig: {
      ...footerConfig,
      useFilteredData: footerConfig?.useFilteredData !== false, // Default to true
    },
  });

  // Apply table configuration
  const applyCompleteConfig = (config: TablePreset['config']) => {
    if (config.visibleColumns && onApplyVisibleColumns) {
      onApplyVisibleColumns(config.visibleColumns);
    }
    if (config.tableDensity && onApplyDensity) {
      onApplyDensity(config.tableDensity);
    }
    if (onApplyTableConfig) {
      onApplyTableConfig({
        columnWidths: config.columnWidths || {},
        columnOrder: config.columnOrder || [],
        sortConfig: config.sortConfig || [],
      });
    }
    if (config.columnLabels && onApplyColumnLabels) {
      onApplyColumnLabels(config.columnLabels);
    } else if (onApplyColumnLabels && (config.columnLabels === undefined || Object.keys(config.columnLabels || {}).length === 0)) {
      onApplyColumnLabels({});
    }
    if (config.footerConfig && onApplyFooterConfig) {
      onApplyFooterConfig(config.footerConfig);
    }
  };

  // Save current configuration (update existing or create new). If create returns "name exists", update that preset instead.
  const handleSavePreset = async () => {
    if (!presetName.trim()) return;

    setIsLoading(true);
    try {
      if (editingPreset) {
        await updateTablePreset(editingPreset.id, {
          name: presetName.trim(),
          description: presetDescription.trim() || undefined,
          config: getCurrentConfig(),
        }, tableId);
        showNotification('Table preset updated successfully', 'success');
      } else {
        const configToSave = duplicateFromConfig ?? getCurrentConfig();
        try {
          await createTablePreset({
            moduleName: tableId,
            name: presetName.trim(),
            description: presetDescription.trim() || undefined,
            config: configToSave,
            isDefault: false,
          });
          showNotification('Table preset saved successfully', 'success');
        } catch (createError: any) {
          if (createError.response?.status === 409) {
            const existing = presets.find((p) => p.name === presetName.trim());
            if (existing) {
              await updateTablePreset(existing.id, { config: getCurrentConfig() }, tableId);
              showNotification(`"${existing.name}" updated with current configuration`, 'success');
            } else {
              throw createError;
            }
          } else {
            throw createError;
          }
        }
      }

      await mutatePresets();
      if (onPresetsChange) onPresetsChange();
      setShowSaveModal(false);
      setPresetName('');
      setPresetDescription('');
      setEditingPreset(null);
      setDuplicateFromConfig(null);
    } catch (error: any) {
      console.error('Error saving table preset:', error);
      showNotification(
        error.response?.data?.message || 'Failed to save table preset',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Show delete confirmation
  const handleDeletePreset = (preset: TablePreset) => {
    setPresetToDelete(preset);
    setShowDeleteConfirm(true);
  };

  // Confirm delete preset
  const confirmDeletePreset = async () => {
    if (!presetToDelete) return;

    setIsLoading(true);
    try {
      await deleteTablePreset(presetToDelete.id, tableId);
      showNotification('Table preset deleted successfully', 'success');
      
      // Refresh presets list
      await mutatePresets();
      if (onPresetsChange) {
        onPresetsChange();
      }

      setShowDeleteConfirm(false);
      setPresetToDelete(null);
    } catch (error: any) {
      console.error('Error deleting table preset:', error);
      showNotification(
        error.response?.data?.message || 'Failed to delete table preset',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setPresetToDelete(null);
  };

  // Apply a preset
  const handleApplyPreset = (preset: TablePreset) => {
    applyCompleteConfig(preset.config);
    onPresetApplied?.(preset);
  };

  // Set preset as default
  const handleSetAsDefault = async (presetId: number) => {
    setIsLoading(true);
    try {
      await setDefaultTablePreset(presetId);
      showNotification('Default preset updated successfully', 'success');
      
      // Refresh presets list
      await mutatePresets();
      if (onPresetsChange) {
        onPresetsChange();
      }
    } catch (error: any) {
      console.error('Error setting default preset:', error);
      showNotification(
        error.response?.data?.message || 'Failed to set default preset',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Start editing a preset
  const startEditPreset = (preset: TablePreset) => {
    // Load the preset's configuration into the current table state
    // so any changes are based on that preset and will be saved back.
    applyCompleteConfig(preset.config);

    setEditingPreset(preset);
    setPresetName(preset.name);
    setPresetDescription(preset.description || '');
    setDuplicateFromConfig(null);
    setShowSaveModal(true);
  };

  // Save current configuration directly to the given preset (or active preset) – no modal
  const handleSaveCurrentToAppliedPreset = async (preset?: TablePreset | null) => {
    const target = preset ?? lastAppliedPreset ?? activePreset;
    if (!target) return;
    setIsLoading(true);
    try {
      await updateTablePreset(target.id, { config: getCurrentConfig() }, tableId);
      showNotification(`"${target.name}" updated`, 'success');
      await mutatePresets();
      if (onPresetsChange) onPresetsChange();
    } catch (error: any) {
      console.error('Error saving table preset:', error);
      showNotification(
        error.response?.data?.message || 'Failed to save preset',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Get table configuration summary for display
  const getConfigSummary = (config: TablePreset['config']) => {
    const parts = [];

    // Count visible columns
    const totalColumns = Object.keys(config.visibleColumns || {}).length;
    const visibleCount = Object.values(config.visibleColumns || {}).filter(Boolean).length;
    const hiddenCount = totalColumns - visibleCount;

    if (totalColumns > 0) {
      if (hiddenCount === 0) {
        parts.push(`all ${visibleCount} columns visible`);
      } else {
        parts.push(`${visibleCount}/${totalColumns} columns visible`);
      }
    }

    // Table density
    if (config.tableDensity && config.tableDensity !== APP_CONFIG.table.defaultDensity) {
      parts.push(`${config.tableDensity} density`);
    } else if (config.tableDensity === APP_CONFIG.table.defaultDensity) {
      parts.push(`${APP_CONFIG.table.defaultDensity} density`);
    }

    // Sort configuration
    if (config.sortConfig && config.sortConfig.length > 0) {
      const sortDescriptions = config.sortConfig
        .sort((a, b) => (a.priority || 1) - (b.priority || 1))
        .map((sort) => `${sort.key} ${sort.direction}`)
        .slice(0, 2);

      if (config.sortConfig.length === 1) {
        parts.push(`sorted by ${sortDescriptions[0]}`);
      } else {
        parts.push(
          `${config.sortConfig.length} sorts: ${sortDescriptions.join(', ')}${
            config.sortConfig.length > 2 ? '...' : ''
          }`
        );
      }
    }

    // Column customizations
    const customizations = [];
    if (config.columnWidths && Object.keys(config.columnWidths).length > 0) {
      customizations.push('sizing');
    }
    if (config.columnOrder && config.columnOrder.length > 0) {
      customizations.push('order');
    }
    if (config.columnLabels && Object.keys(config.columnLabels).length > 0) {
      customizations.push('renamed columns');
    }

    if (customizations.length > 0) {
      parts.push(`custom ${customizations.join(' & ')}`);
    }

    return parts.length > 0 ? parts.join(', ') : 'Default configuration';
  };

  // Check if current configuration matches a preset
  // Use a more robust comparison that handles object key ordering
  const isPresetActive = (preset: TablePreset) => {
    const current = getCurrentConfig();
    const presetConfig = preset.config;
    
    // Compare visible columns (order-independent)
    const currentVisibleKeys = Object.keys(current.visibleColumns || {}).sort();
    const presetVisibleKeys = Object.keys(presetConfig.visibleColumns || {}).sort();
    if (currentVisibleKeys.length !== presetVisibleKeys.length) return false;
    for (const key of currentVisibleKeys) {
      if (current.visibleColumns[key] !== presetConfig.visibleColumns[key]) return false;
    }
    
    // Compare other properties
    if (current.tableDensity !== presetConfig.tableDensity) return false;
    
    // Compare column widths (order-independent)
    const currentWidthsObj = current.columnWidths || {};
    const presetWidthsObj = presetConfig.columnWidths || {};
    const currentWidths = JSON.stringify(
      Object.keys(currentWidthsObj).sort().reduce((acc, key) => {
        acc[key] = currentWidthsObj[key];
        return acc;
      }, {} as Record<string, string>)
    );
    const presetWidths = JSON.stringify(
      Object.keys(presetWidthsObj).sort().reduce((acc, key) => {
        acc[key] = presetWidthsObj[key];
        return acc;
      }, {} as Record<string, string>)
    );
    if (currentWidths !== presetWidths) return false;
    
    // Compare column order (as arrays, order matters)
    const currentOrder = JSON.stringify(current.columnOrder || []);
    const presetOrder = JSON.stringify(presetConfig.columnOrder || []);
    if (currentOrder !== presetOrder) return false;
    
    // Compare sort config (order by priority)
    const currentSort = JSON.stringify(
      (current.sortConfig || []).slice().sort((a, b) => (a.priority || 1) - (b.priority || 1))
    );
    const presetSort = JSON.stringify(
      (presetConfig.sortConfig || []).slice().sort((a, b) => (a.priority || 1) - (b.priority || 1))
    );
    if (currentSort !== presetSort) return false;
    
    // Compare footer config
    const currentFooter = JSON.stringify(current.footerConfig || {});
    const presetFooter = JSON.stringify(presetConfig.footerConfig || {});
    if (currentFooter !== presetFooter) return false;

    // Compare column labels (order-independent)
    const currentLabels = JSON.stringify(current.columnLabels || {});
    const presetLabels = JSON.stringify(presetConfig.columnLabels || {});
    if (currentLabels !== presetLabels) return false;
    
    return true;
  };

  // Active preset: explicitly selected, or matching current config, or sticky (so after changes we still show "Save to [preset]")
  const matchedPreset = presets.find((p) => isPresetActive(p)) ?? null;
  const activePreset =
    lastAppliedPreset ?? matchedPreset ?? stickyActivePreset;

  const hasUnsavedChangesToAppliedPreset =
    activePreset != null && !isPresetActive(activePreset);

  // Keep sticky active preset in sync: when we have an explicit or matching preset, remember it so it stays visible after user makes changes
  useEffect(() => {
    if (!isOpen) {
      setStickyActivePreset(null);
      return;
    }
    const next = lastAppliedPreset ?? matchedPreset ?? null;
    if (next) setStickyActivePreset(next);
  }, [isOpen, lastAppliedPreset, matchedPreset]);

  const hasCustomizations = () => {
    const config = getCurrentConfig();
    const hasHiddenColumns = Object.values(config.visibleColumns).some((v) => v === false);
    const hasCustomDensity = config.tableDensity !== APP_CONFIG.table.defaultDensity;
    const hasCustomWidths = Object.keys(config.columnWidths || {}).length > 0;
    const hasCustomOrder = (config.columnOrder || []).length > 0;
    const hasCustomLabels = Object.keys(config.columnLabels || {}).length > 0;
    const hasCustomSort = (config.sortConfig || []).length > 0;
    const hasFooterConfig = config.footerConfig?.showFooter === true && 
      Object.keys(config.footerConfig?.footerColumns || {}).length > 0;

    return (
      hasHiddenColumns || hasCustomDensity || hasCustomWidths || hasCustomOrder || hasCustomLabels || hasCustomSort || hasFooterConfig
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {!asPanel && (
        <div
          className="fixed inset-0 z-[95] bg-black/30 dark:bg-black/40"
          onClick={onClose}
          aria-hidden
        />
      )}

      {/* Main presets panel: fixed overlay when !asPanel, fills parent when asPanel */}
      <div
        ref={containerRef}
        className={cn(
          'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col',
          asPanel
            ? 'h-full min-h-0 w-full'
            : 'fixed inset-0 right-0 max-w-3xl w-full sm:w-[90vw] z-[100]'
        )}
      >
        {/* Header */}
        <div className={cn(
          'flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b',
          'bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
          'border-gray-200 dark:border-gray-700 flex-shrink-0'
        )}>
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="p-1.5 sm:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex-shrink-0">
              <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={`text-sm sm:text-base font-bold ${colors.text} truncate`}>
                {entityName} Table Presets
              </h3>
              <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-0.5 hidden sm:block`}>
                Customize your table layout and save presets
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={cn(
              'h-8 w-8 sm:h-9 sm:w-9 p-0 rounded-lg transition-all flex-shrink-0',
              `${colors.textMuted} hover:${colors.textSecondary}`,
              'hover:bg-gray-100 dark:hover:bg-gray-700',
              'flex items-center justify-center ml-2'
            )}
            title="Close"
          >
            <XIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-3 sm:px-6 flex-shrink-0">
            <button
              onClick={() => setActiveTab('customize')}
              className={cn(
                'px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold border-b-2 transition-all relative',
                activeTab === 'customize'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-800'
                  : `border-transparent ${colors.textMuted} hover:${colors.textSecondary}`
              )}
            >
              <span className="flex items-center gap-1.5 sm:gap-2">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                <span className="hidden sm:inline">Customize</span>
                <span className="sm:hidden">Custom</span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('presets')}
              className={cn(
                'px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold border-b-2 transition-all relative flex items-center gap-1.5 sm:gap-2',
                activeTab === 'presets'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-800'
                  : `border-transparent ${colors.textMuted} hover:${colors.textSecondary}`
              )}
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h12a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">Saved Presets</span>
              <span className="sm:hidden">Presets</span>
              {presets.length > 0 && (
                <span className={cn(
                  'inline-flex items-center justify-center px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-bold rounded-full',
                  activeTab === 'presets'
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                )}>
                  {presets.length}
                </span>
              )}
            </button>
          </div>

          {/* Save current – compact summary bar with active preset pill */}
          <div
            className={cn(
              'flex-shrink-0 px-3 sm:px-6 py-2.5 sm:py-3 border-b',
              colors.border,
              activePreset
                ? 'bg-amber-50/90 dark:bg-amber-900/20'
                : 'bg-gray-50/90 dark:bg-gray-900/40'
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="hidden sm:flex items-center justify-center w-6 h-6 rounded-full bg-white/80 dark:bg-gray-800/80 border border-amber-200/80 dark:border-amber-700/70 flex-shrink-0">
                <CheckIcon className={cn('w-3.5 h-3.5', activePreset ? 'text-amber-600' : 'text-gray-500')} />
              </div>
              <div className="min-w-0 flex-1">
                <div className={cn('text-[11px] sm:text-xs font-semibold leading-snug truncate', colors.text)}>
                  {activePreset ? (
                    <>
                      Save current table layout
                      <span className={cn('ml-2', colors.textMuted)}>
                        Active preset: "{activePreset.name}"
                      </span>
                    </>
                  ) : (
                    'Save current table layout as a preset'
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1.5 flex-wrap justify-end">
              {activePreset ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSaveCurrentToAppliedPreset(activePreset)}
                    disabled={isLoading}
                    className={cn(
                      'h-8 px-2.5 sm:px-3 text-[10px] sm:text-xs font-semibold rounded-full',
                      'bg-white dark:bg-gray-900 border-amber-300 dark:border-amber-700',
                      'text-amber-800 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/40'
                    )}
                  >
                    <CheckIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                    Save to preset
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingPreset(null);
                      setPresetName('');
                      setPresetDescription('');
                      setDuplicateFromConfig(null);
                      setShowSaveModal(true);
                    }}
                    className="h-8 px-2.5 sm:px-3 text-[10px] sm:text-xs font-medium rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <CopyIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                    Save as new
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDuplicateFromConfig(null);
                    setShowSaveModal(true);
                  }}
                  className="h-8 px-2.5 sm:px-3 text-[10px] sm:text-xs font-semibold rounded-full bg-white dark:bg-gray-900 border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/40"
                >
                  <CopyIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                  Save preset
                </Button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
          {activeTab === 'customize' && (
            <div className="space-y-3 sm:space-y-4 lg:space-y-5">
              <Accordion className="space-y-3">
              {/* Table Density */}
              <AccordionItem
                title="Table Density"
                description="Adjust row spacing"
                open={accordionOpen.density}
                onToggle={(open) => setAccordionSection('density', open)}
              >
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {Object.entries(APP_CONFIG.table.densityConfig).map(([key, config]) => (
                    <label
                      key={key}
                      className={cn(
                        'relative flex flex-col p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all',
                        tableDensity === key
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 shadow-sm'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      )}
                    >
                      <input
                        type="radio"
                        name="density"
                        value={key}
                        checked={tableDensity === key}
                        onChange={() => onApplyDensity(key as 'compact' | 'normal' | 'comfortable')}
                        className="sr-only"
                      />
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                        <div className={cn(
                          'w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                          tableDensity === key
                            ? 'border-indigo-500 bg-indigo-500'
                            : 'border-gray-300 dark:border-gray-600'
                        )}>
                          {tableDensity === key && (
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <span className={cn(
                          'text-xs sm:text-sm font-semibold capitalize truncate',
                          tableDensity === key
                            ? 'text-indigo-700 dark:text-indigo-300'
                            : 'text-gray-700 dark:text-gray-300'
                        )}>
                          {key}
                        </span>
                      </div>
                      <p className={`text-[10px] sm:text-xs ${colors.textMuted} ml-4.5 sm:ml-6 line-clamp-2`}>
                        {config.description}
                      </p>
                    </label>
                  ))}
                </div>
              </AccordionItem>

              {/* Visible Columns */}
              <AccordionItem
                title="Visible Columns"
                description="Show, hide, rename and reorder"
                open={accordionOpen.columns}
                onToggle={(open) => setAccordionSection('columns', open)}
              >
                <div className={cn('flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-2', colors.border)}>
                  <span className={cn('text-xs font-medium tabular-nums', colors.textMuted)}>
                    {Object.values(visibleColumns).filter(Boolean).length} of {Object.keys(visibleColumns).length} visible
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allVisible = allColumns.reduce(
                        (acc, col) => ({ ...acc, [col.key]: true }),
                        {}
                      );
                      onApplyVisibleColumns(allVisible);
                    }}
                    className="text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10"
                  >
                    Show all
                  </Button>
                </div>
                {allColumns.length > 4 && (
                  <div className="mb-2 relative">
                    <input
                      type="text"
                      placeholder="Filter columns…"
                      value={columnFilter}
                      onChange={(e) => setColumnFilter(e.target.value)}
                      className={cn(
                        'w-full px-2.5 py-1.5 pr-8 text-xs rounded-md border bg-white dark:bg-gray-800 placeholder-gray-400 dark:placeholder-gray-500',
                        colors.border,
                        colors.text,
                        'focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500'
                      )}
                    />
                    {columnFilter.trim() && (
                      <button
                        type="button"
                        onClick={() => setColumnFilter('')}
                        className={cn(
                          'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
                          'focus:outline-none focus:ring-2 focus:ring-emerald-500/30'
                        )}
                        title="Clear filter"
                        aria-label="Clear filter"
                      >
                        <XIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
                {/* Column list: drag | checkbox | name (with inline edit) | [optional] max width */}
                {(() => {
                  const hasLabels = !!onApplyColumnLabels;
                  const hasWidth = !!onApplyTableConfig;
                  const gridCols = hasWidth
                    ? '24px 28px 1fr 70px'
                    : '24px 28px 1fr';
                  const filterLower = columnFilter.trim().toLowerCase();
                  const filteredColumns = filterLower
                    ? orderedColumns.filter(
                        (c) =>
                          c.key.toLowerCase().includes(filterLower) ||
                          getColumnHeaderText(c.header).toLowerCase().includes(filterLower),
                      )
                    : orderedColumns;
                  return (
                    <>
                      {hasWidth && filteredColumns.length > 0 && (
                        <div className={cn('hidden sm:grid gap-2 px-4 py-2 text-xs font-medium border-b', colors.border, colors.textMuted)} style={{ gridTemplateColumns: gridCols }}>
                          <span /><span /><span>Column</span>
                          <span>Width</span>
                        </div>
                      )}
                      <div className="max-h-80 overflow-y-auto">
                        {filteredColumns.length === 0 ? (
                          <p className={cn('py-4 text-center text-xs', colors.textMuted)}>
                            {filterLower ? 'No columns match the filter' : 'No columns'}
                          </p>
                        ) : (
                          filteredColumns.map((column) => {
                          const isVisible = visibleColumns[column.key] !== false;
                          const isDragOver = dragOverColumnKey === String(column.key);
                          return (
                            <div
                              key={column.key}
                              className={cn(
                                'grid gap-2 items-center px-4 py-2.5 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 transition-colors',
                                isDragOver && 'bg-indigo-50 dark:bg-indigo-900/20',
                                !isDragOver && (isVisible ? 'bg-white dark:bg-gray-800/50' : 'bg-gray-50/80 dark:bg-gray-800/30')
                              )}
                              style={{ gridTemplateColumns: gridCols }}
                              draggable
                              onDragStart={() => setDraggingColumnKey(String(column.key))}
                              onDragOver={(e) => {
                                e.preventDefault();
                                if (dragOverColumnKey !== String(column.key)) setDragOverColumnKey(String(column.key));
                              }}
                              onDragLeave={() => dragOverColumnKey === String(column.key) && setDragOverColumnKey(null)}
                              onDrop={(e) => {
                                e.preventDefault();
                                handleColumnDrop(String(column.key));
                                setDragOverColumnKey(null);
                              }}
                            >
                              <div className="cursor-grab active:cursor-grabbing flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400" title="Drag to reorder">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 6h2v2H8V6zm0 5h2v2H8v-2zm0 5h2v2H8v-2zm5-10h2v2h-2V6zm0 5h2v2h-2v-2zm0 5h2v2h-2v-2z" /></svg>
                              </div>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isVisible}
                                  onChange={(e) => {
                                    const next = { ...visibleColumns, [column.key]: e.target.checked };
                                    onApplyVisibleColumns(next);
                                  }}
                                  className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500"
                                />
                              </label>
                              <div className="flex items-center gap-2 min-w-0">
                                {editingColumnKeyForLabel === String(column.key) && hasLabels ? (
                                  <>
                                    <TextInput
                                      type="text"
                                      value={editingColumnLabel}
                                      autoFocus
                                      onChange={(e) => {
                                        setEditingColumnLabel(e.target.value);
                                      }}
                                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const value = editingColumnLabel.trim();
                                          const next = { ...(columnLabels || {}) };
                                          if (value && value !== getColumnHeaderText(column.header)) next[column.key] = value;
                                          else delete next[column.key];
                                          onApplyColumnLabels?.(next);
                                          setEditingColumnKeyForLabel(null);
                                          setEditingColumnLabel('');
                                        } else if (e.key === 'Escape') {
                                          e.preventDefault();
                                          setEditingColumnKeyForLabel(null);
                                          setEditingColumnLabel('');
                                        }
                                      }}
                                      className="h-8 text-sm"
                                      placeholder={getColumnHeaderText(column.header)}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const value = editingColumnLabel.trim();
                                        const next = { ...(columnLabels || {}) };
                                        if (value && value !== getColumnHeaderText(column.header)) next[column.key] = value;
                                        else delete next[column.key];
                                        onApplyColumnLabels?.(next);
                                        setEditingColumnKeyForLabel(null);
                                        setEditingColumnLabel('');
                                      }}
                                      className={cn(
                                        'p-1 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors',
                                        colors.textSecondary
                                      )}
                                      title="Save name"
                                    >
                                      <CheckIcon className="w-4 h-4 text-green-600" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingColumnKeyForLabel(null);
                                        setEditingColumnLabel('');
                                      }}
                                      className={cn(
                                        'p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors',
                                        colors.textSecondary
                                      )}
                                      title="Cancel"
                                    >
                                      <XIcon className="w-4 h-4" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span className={cn('text-sm truncate min-w-0', isVisible ? 'font-medium ' + colors.text : colors.textMuted)}>
                                      {(columnLabels && columnLabels[column.key]) || getColumnHeaderText(column.header)}
                                    </span>
                                    {hasLabels && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const current =
                                            (columnLabels && columnLabels[column.key]) ||
                                            getColumnHeaderText(column.header);
                                          setEditingColumnKeyForLabel(String(column.key));
                                          setEditingColumnLabel(current);
                                        }}
                                        className={cn(
                                          'p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0',
                                          colors.textSecondary
                                        )}
                                        title="Rename column"
                                      >
                                        <PencilIcon className="w-4 h-4" />
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                              {hasWidth && (
                                <input
                                  type="number"
                                  min={50}
                                  max={1000}
                                  value={columnWidths?.[column.key] ? parseInt(columnWidths[column.key], 10) || '' : ''}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    const next = { ...(columnWidths || {}) };
                                    if (!raw) delete next[column.key];
                                    else next[column.key] = `${raw}px`;
                                    onApplyTableConfig?.({ columnWidths: next });
                                  }}
                                  placeholder="—"
                                  className={cn('w-full px-2.5 py-1.5 text-xs rounded-md border bg-white dark:bg-gray-800 tabular-nums', colors.border, colors.text, 'focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500')}
                                />
                              )}
                            </div>
                          );
                        })
                        )}
                      </div>
                    </>
                  );
                })()}
              </AccordionItem>

              {/* Current Sort */}
              <AccordionItem
                title="Current Sort"
                description={sortConfig?.length ? `${sortConfig.length} column(s)` : 'View or clear active sort'}
                open={accordionOpen.sort}
                onToggle={(open) => setAccordionSection('sort', open)}
              >
                {sortConfig && sortConfig.length > 0 ? (
                  <>
                    <div className="flex items-center justify-end mb-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onApplyTableConfig?.({ sortConfig: [] })}
                        className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Clear Sort
                      </Button>
                    </div>
                    <div className="space-y-1">
                      {sortConfig
                        .sort((a, b) => (a.priority || 1) - (b.priority || 1))
                        .map((sort, index) => {
                          const columnHeader = allColumns.find((c) => String(c.key) === sort.key)?.header ?? sort.key;
                          return (
                          <div key={sort.key} className={cn('flex items-center justify-between text-sm', colors.text)}>
                            <span className="flex items-center">
                              <span
                                className={cn(
                                  'inline-flex items-center justify-center w-4 h-4 rounded-full text-xs mr-2',
                                  colors.surface,
                                  colors.border
                                )}
                              >
                                {sort.priority || index + 1}
                              </span>
                              <span className="font-medium">{columnHeader}</span>
                            </span>
                            <span className={cn('flex items-center capitalize', colors.textSecondary)}>
                              {sort.direction === 'asc' ? '↑' : '↓'}
                              <span className="ml-1">{sort.direction}</span>
                            </span>
                          </div>
                          );
                        })}
                    </div>
                  </>
                ) : (
                  <p className={cn('text-xs', colors.textMuted)}>
                    No sort applied. Click column headers in the table to sort.
                  </p>
                )}
              </AccordionItem>

              {/* Table Footer */}
              <AccordionItem
                title="Table Footer"
                description="Configure aggregations"
                open={accordionOpen.footer}
                onToggle={(open) => setAccordionSection('footer', open)}
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={footerConfig?.showFooter || false}
                      onChange={(e) => {
                        if (onApplyFooterConfig) {
                          onApplyFooterConfig({
                            ...footerConfig,
                            showFooter: e.target.checked,
                            useFilteredData: footerConfig?.useFilteredData !== false,
                            footerColumns: footerConfig?.footerColumns || {},
                          });
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                    <span className={cn('ml-2 sm:ml-3 text-xs sm:text-sm font-medium', colors.textSecondary)}>
                      {footerConfig?.showFooter ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
                {footerConfig?.showFooter && (
                  <div className="mb-3 sm:mb-4 p-2 sm:p-2.5 bg-purple-50 dark:bg-purple-900/10 rounded-lg border border-purple-200 dark:border-purple-900/30">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={footerConfig?.useFilteredData !== false}
                        onChange={(e) => {
                          if (onApplyFooterConfig) {
                            onApplyFooterConfig({
                              ...footerConfig,
                              useFilteredData: e.target.checked,
                            });
                          }
                        }}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-[10px] sm:text-xs text-gray-700 dark:text-gray-300">
                        Calculate from filtered data (uncheck to use all data)
                      </span>
                    </label>
                  </div>
                )}
                {footerConfig?.showFooter && (
                  <div className="space-y-2 sm:space-y-3">
                    <p className={`text-[10px] sm:text-xs ${colors.textMuted} bg-purple-50 dark:bg-purple-900/10 p-2 rounded-lg`}>
                      Configure footer aggregations for each column (sum, average, or count)
                    </p>
                    <div className="space-y-2 sm:space-y-2.5 max-h-48 sm:max-h-64 overflow-y-auto pr-1">
                      {allColumns
                        .filter(col => visibleColumns[col.key] !== false)
                        .map((column) => {
                          const columnFooter = footerConfig?.footerColumns?.[column.key];
                          return (
                            <div
                              key={column.key}
                              className="p-2 sm:p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-2">
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
                                  {getColumnHeaderText(column.header)}
                                </span>
                                <select
                                  value={columnFooter?.footerType || ''}
                                  onChange={(e) => {
                                    if (onApplyFooterConfig) {
                                      const newFooterColumns = {
                                        ...(footerConfig?.footerColumns || {}),
                                      };
                                      if (e.target.value) {
                                        newFooterColumns[column.key] = {
                                          footerType: e.target.value as FooterType,
                                          footerLabel: columnFooter?.footerLabel || '',
                                        };
                                      } else {
                                        delete newFooterColumns[column.key];
                                      }
                                      onApplyFooterConfig({
                                        ...footerConfig,
                                        footerColumns: newFooterColumns,
                                      });
                                    }
                                  }}
                                  className={`text-[10px] sm:text-xs border ${colors.border} rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 ${colors.surface} ${colors.text} focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-full sm:w-auto`}
                                >
                                  <option value="">No Footer</option>
                                  <option value="sum">Sum</option>
                                  <option value="avg">Average</option>
                                  <option value="count">Count</option>
                                </select>
                              </div>
                              {columnFooter?.footerType && (
                                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200 dark:border-gray-700">
                                  <label className={`text-[10px] sm:text-xs font-medium ${colors.textSecondary} block mb-1 sm:mb-1.5`}>
                                    Custom Label (optional)
                                  </label>
                                  <input
                                    type="text"
                                    value={columnFooter?.footerLabel || ''}
                                    onChange={(e) => {
                                      if (onApplyFooterConfig) {
                                        const newFooterColumns = {
                                          ...(footerConfig?.footerColumns || {}),
                                          [column.key]: {
                                            ...columnFooter,
                                            footerLabel: e.target.value,
                                          },
                                        };
                                        onApplyFooterConfig({
                                          ...footerConfig,
                                          footerColumns: newFooterColumns,
                                        });
                                      }
                                    }}
                                    placeholder={columnFooter.footerType === 'sum' ? 'Total' : 
                                      columnFooter.footerType === 'avg' ? 'Average' : 
                                      columnFooter.footerType === 'count' ? 'Count' : ''}
                                    className="w-full text-[10px] sm:text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </AccordionItem>
              </Accordion>

              {/* Tips & Instructions */}
              <AccordionItem
                title="Tips & Instructions"
                description="How to use table presets"
                open={accordionOpen.tips}
                onToggle={(open) => setAccordionSection('tips', open)}
              >
                <ul className="space-y-1.5 text-[10px] sm:text-xs text-blue-800 dark:text-blue-400">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Click column headers in the table to sort data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Use checkboxes above to show or hide columns</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Adjust table density for better viewing experience</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Configure footer aggregations (sum, average, count) for columns</span>
                  </li>
                </ul>
              </AccordionItem>
            </div>
          )}

          {activeTab === 'presets' && (
            <div className="space-y-3 sm:space-y-4 lg:space-y-5">
              {/* Update applied preset (when user applied a preset and made changes) */}
              {hasUnsavedChangesToAppliedPreset && activePreset && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 sm:p-4 bg-amber-50 dark:bg-amber-900/10 rounded-lg sm:rounded-xl border border-amber-200 dark:border-amber-800">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs sm:text-sm font-semibold text-amber-900 dark:text-amber-100 mb-0.5 sm:mb-1">
                      Unsaved changes to &quot;{activePreset.name}&quot;
                    </h4>
                    <p className="text-[10px] sm:text-xs text-amber-700 dark:text-amber-300">
                      Save directly to this preset or save as a new one
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSaveCurrentToAppliedPreset(activePreset)}
                      disabled={isLoading}
                      className="text-[10px] sm:text-xs bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-800 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 w-full sm:w-auto"
                    >
                      <CheckIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                      Save to &quot;{activePreset.name}&quot;
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingPreset(null);
                        setPresetName('');
                        setPresetDescription('');
                        setDuplicateFromConfig(null);
                        setShowSaveModal(true);
                      }}
                      className="text-[10px] sm:text-xs text-amber-700 dark:text-amber-300 w-full sm:w-auto"
                    >
                      <CopyIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                      Save as new preset
                    </Button>
                  </div>
                </div>
              )}

              {/* Saved Presets */}
              <div className="space-y-2 sm:space-y-3">
                {isLoadingPresets ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={cn('h-24 sm:h-28 rounded-lg animate-pulse', colors.surface)}
                      />
                    ))}
                  </div>
                ) : (() => {
                  const sortedPresets = [...presets].sort((a, b) => {
                    if (a.isDefault && !b.isDefault) return -1;
                    if (!a.isDefault && b.isDefault) return 1;
                    const aActive = isPresetActive(a);
                    const bActive = isPresetActive(b);
                    if (aActive && !bActive) return -1;
                    if (!aActive && bActive) return 1;
                    return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
                  });
                  return (
                  presets.length === 0 ? (
                  <div className="text-center py-8 sm:py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg sm:rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700">
                    <div className="p-2 sm:p-3 bg-gray-100 dark:bg-gray-700 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                      <FolderPlusIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 dark:text-gray-500" />
                    </div>
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white mb-1">No saved presets</p>
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-3 sm:mb-4">
                      Customize the table and save your first preset
                    </p>
                    {hasCustomizations() && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDuplicateFromConfig(null);
                          setShowSaveModal(true);
                        }}
                        className="text-[10px] sm:text-xs"
                      >
                        <CopyIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                        Create First Preset
                      </Button>
                    )}
                  </div>
                ) : (
                  sortedPresets.map((preset) => {
                    const isDefault = preset.isDefault;
                    const isActive = isPresetActive(preset);
                    return (
                      <div
                        key={preset.id}
                        className={cn(
                          'group relative p-3 sm:p-4 lg:p-5 border-2 rounded-lg sm:rounded-xl transition-all duration-200',
                          isActive
                            ? 'border-l-4 border-l-indigo-500 bg-indigo-50 dark:bg-indigo-900/25 border-indigo-200 dark:border-indigo-700 shadow-md'
                            : isDefault
                            ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-300 dark:border-green-700 shadow-md hover:shadow-lg hover:scale-[1.005]'
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700 hover:scale-[1.005]'
                        )}
                      >
                        <div className="flex flex-col gap-3">
                          {/* Row 1: Icon + name (truncated) on left; Apply button on right */}
                          <div className="flex items-center justify-between gap-3 min-w-0">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <div className={cn(
                                'p-1 sm:p-1.5 rounded-lg flex-shrink-0',
                                isActive
                                  ? 'bg-indigo-200 dark:bg-indigo-700'
                                  : isDefault
                                  ? 'bg-green-200 dark:bg-green-800'
                                  : 'bg-indigo-100 dark:bg-indigo-900/30'
                              )}>
                                <DocumentTextIcon className={cn(
                                  'w-3.5 h-3.5 sm:w-4 sm:h-4',
                                  isActive
                                    ? 'text-indigo-700 dark:text-indigo-200'
                                    : isDefault
                                    ? 'text-green-700 dark:text-green-300'
                                    : 'text-indigo-600 dark:text-indigo-400'
                                )} />
                              </div>
                              <h4
                                className={cn(
                                  'text-xs sm:text-sm font-bold truncate min-w-0',
                                  isActive
                                    ? 'text-indigo-900 dark:text-indigo-100'
                                    : isDefault
                                    ? 'text-green-800 dark:text-green-200'
                                    : 'text-gray-900 dark:text-white'
                                )}
                              >
                                {preset.name}
                              </h4>
                            </div>
                            <div className="flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApplyPreset(preset)}
                                disabled={isActive}
                                className={cn(
                                  'h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs font-medium',
                                  isActive
                                    ? 'text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-800/50 border border-indigo-300 dark:border-indigo-600 cursor-default'
                                    : isDefault
                                    ? 'text-green-700 hover:text-green-900 hover:bg-green-100 dark:text-green-300 dark:hover:text-green-100 dark:hover:bg-green-800/30 border border-green-300 dark:border-green-700'
                                    : 'text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800'
                                )}
                              >
                                <CheckIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                                {isActive ? 'Applied' : 'Apply'}
                              </Button>
                            </div>
                          </div>

                          {/* Row 2: Active / Default badges only */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {isActive && (
                              <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold bg-indigo-200 dark:bg-indigo-700 text-indigo-800 dark:text-indigo-100 border border-indigo-300 dark:border-indigo-600 flex-shrink-0">
                                Active
                              </span>
                            )}
                            {isDefault && !isActive && (
                              <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200 border border-green-300 dark:border-green-700 flex-shrink-0">
                                Default
                              </span>
                            )}
                          </div>

                          {preset.description && (
                            <p
                              className={cn(
                                'text-[10px] sm:text-xs truncate',
                                isActive
                                  ? 'text-indigo-700 dark:text-indigo-300'
                                  : isDefault
                                  ? 'text-green-700 dark:text-green-300'
                                  : 'text-gray-500 dark:text-gray-400'
                              )}
                            >
                              {preset.description}
                            </p>
                          )}

                          {/* Action buttons row */}
                          <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
                            {!isDefault && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSetAsDefault(preset.id)}
                                disabled={isLoading}
                                className={cn(
                                  'h-7 sm:h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs font-medium',
                                  isActive
                                    ? 'text-amber-700 dark:text-amber-300 hover:bg-indigo-100 dark:hover:bg-indigo-800/50'
                                    : 'text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                                )}
                                title="Set as default preset"
                              >
                                <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-0.5 sm:mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                <span className="hidden sm:inline">Default</span>
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setDuplicateFromConfig(preset.config);
                                setPresetName(`Copy of ${preset.name}`);
                                setPresetDescription(preset.description || '');
                                setEditingPreset(null);
                                setShowSaveModal(true);
                              }}
                              className={cn(
                                'h-7 sm:h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs font-medium',
                                isActive
                                  ? 'text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800/50'
                                  : isDefault
                                  ? 'text-green-700 hover:text-green-900 hover:bg-green-100 dark:text-green-300 dark:hover:text-green-100 dark:hover:bg-green-800/30'
                                  : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                              )}
                              title="Duplicate preset"
                            >
                              <CopyIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                              <span className="hidden sm:inline">Duplicate</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditPreset(preset)}
                              className={cn(
                                'h-7 sm:h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs font-medium',
                                isActive
                                  ? 'text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800/50'
                                  : isDefault
                                  ? 'text-green-700 hover:text-green-900 hover:bg-green-100 dark:text-green-300 dark:hover:text-green-100 dark:hover:bg-green-800/30'
                                  : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                              )}
                              title="Edit preset"
                            >
                              <PencilIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeletePreset(preset)}
                              className={cn(
                                'h-6 px-2 text-xs',
                                isActive
                                  ? 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30'
                                  : isDefault
                                  ? 'text-red-700 hover:text-red-900 hover:bg-red-100 dark:text-red-300 dark:hover:text-red-100 dark:hover:bg-red-800/30'
                                  : 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                              )}
                              title="Delete preset"
                            >
                              <TrashIcon className="w-3 h-3" />
                              Delete
                            </Button>
                          </div>
                        </div>

                        {/* Summary line with date - full width, truncates with ellipsis */}
                        <div
                          className={cn(
                            'text-xs mt-2 pt-2 flex items-center gap-1 min-w-0 border-t border-gray-200/50 dark:border-gray-700/50',
                            isActive
                              ? 'text-indigo-600 dark:text-indigo-400'
                              : isDefault
                              ? 'text-green-600 dark:text-green-400'
                              : colors.textMuted
                          )}
                        >
                          <DocumentTextIcon className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate min-w-0">
                            {getConfigSummary(preset.config)} • {new Date(preset.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    );
                  })
                  ) );
                })()}
              </div>

              {/* Presets Instructions */}
              <div className={cn('text-xs border-t pt-3 mt-4', colors.textMuted, colors.border)}>
                <p>• Presets save table layout customizations</p>
                <p>• Includes columns, sizing, order, density & sorting</p>
                <p>• Data filters are separate and not saved in presets</p>
                <p>• Click ⭐ to set a preset as default</p>
                <p>• Default presets load automatically when form opens</p>
              </div>
            </div>
          )}
          </div>
      </div>

      {/* Save Preset Modal - z-[110] so it appears above the Table Presets panel (z-[100]) */}
      <Modal
        isOpen={showSaveModal}
        zIndex={110}
        onClose={() => {
          setShowSaveModal(false);
          setEditingPreset(null);
          setPresetName('');
          setPresetDescription('');
        }}
      >
        <ModalHeader>
          <ModalTitle>
            {editingPreset ? 'Update Table Preset' : 'Save Table Preset'}
          </ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <label className={cn('block text-sm font-medium mb-1', colors.text)}>
                Preset Name *
              </label>
              <TextInput
                name="presetName"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g., Active Containers View"
                className="w-full"
              />
            </div>
            <div>
              <label className={cn('block text-sm font-medium mb-1', colors.text)}>
                Description (Optional)
              </label>
              <TextInput
                name="presetDescription"
                value={presetDescription}
                onChange={(e) => setPresetDescription(e.target.value)}
                placeholder="e.g., Filtered view with custom columns for daily operations"
                className="w-full"
              />
            </div>
            <div className={cn('p-3 rounded-lg', colors.surface, colors.border, 'border')}>
              <div className={cn('text-sm font-medium mb-2', colors.text)}>
                What will be saved:
              </div>
              <div className={cn('text-sm space-y-1', colors.textSecondary)}>
                <div className={cn('font-medium', colors.text)}>
                  {getConfigSummary(getCurrentConfig())}
                </div>
                <div className="text-xs mt-2 space-y-1">
                  <div>✓ Table density: {tableDensity}</div>
                  <div>
                    ✓ Column visibility:{' '}
                    {Object.values(visibleColumns).filter(Boolean).length} of{' '}
                    {Object.keys(visibleColumns).length} columns shown
                  </div>
                  {columnOrder && columnOrder.length > 0 && (
                    <div>✓ Column order: Custom arrangement</div>
                  )}
                  {sortConfig && sortConfig.length > 0 && (
                    <div>
                      ✓ Sorting: {sortConfig.length} active sort{sortConfig.length !== 1 ? 's' : ''}
                    </div>
                  )}
                  {columnWidths && Object.keys(columnWidths).length > 0 && (
                    <div>
                      ✓ Column sizing: {Object.keys(columnWidths).length} custom width
                      {Object.keys(columnWidths).length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </ModalContent>
        <ModalFooter className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={() => {
              setShowSaveModal(false);
              setEditingPreset(null);
              setPresetName('');
              setPresetDescription('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePreset}
            disabled={!presetName.trim() || isLoading}
          >
            {editingPreset ? 'Update' : 'Save'} Preset
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Dialog - z-[110] so it appears above the Table Presets panel (z-[100]) */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        zIndex={110}
        onClose={cancelDelete}
        onConfirm={confirmDeletePreset}
        title="Delete Preset"
        message={`Are you sure you want to delete the preset "${presetToDelete?.name}"?`}
        details={
          presetToDelete
            ? {
                Name: presetToDelete.name,
                ...(presetToDelete.description && { Description: presetToDelete.description }),
                Configuration: getConfigSummary(presetToDelete.config),
              }
            : null
        }
        warningMessage={`This action cannot be undone.${
          presetToDelete?.isDefault
            ? ' ⚠️ This is your default preset. Deleting it will remove the default setting.'
            : ''
        }`}
        confirmText="Delete Preset"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </>
  );
};

export default TablePresets;

